<template>
  <app-content>
    <app-content-body>

      <div class="inner">
        <p class="buttons">
          <a class="btn btn_action btn_add" :title="translations.addShow" @click.prevent="createItem"></a>
        </p>
        <h1 v-translate>Pořady</h1>
      </div>

      <app-table
        selectable
        :selected-rows="checkedRows"
        clickable
        deletable
        :data="items"
        :highlight-rows-on-hover="true"
        :order="params.order"
        :page-size="params.limit"
        :loading="listLoading || labelsPending"
        @sort="sortItems"
        @rowClick="goToDetail"
        @deleteRow="deleteItem"
        @rowSelect="selectRow"
      >
        <template slot='header'>
          <app-table-header-item class="field_left">
            <app-input
              :id="'show-name-search'"
              :label="translations.searchByName"
              type="search"
              v-model="params.search"
              @input="fetchItems(true)"
            />
            <app-checkbox
                name="fulltextSearch"
                title="Zpřísnit kritéria vyhledávání"
                v-model="fulltextSearch"
            />
          </app-table-header-item>
          <app-table-header-item class="field_right field_search">
            <app-input
              :label="translations.searchByContent"
              type="search"
              v-model="params.searchContent"
              @input="fetchItems(true)"
            />
          </app-table-header-item>
            <app-table-header-item class="field_right">
                <app-table-header-select :label="translations.periodicity">
                    <app-select
                        id="periodicity"
                        :options="periodicOptions"
                        selectId="periodicitySelect"
                        name="periodicitySelect"
                        v-model="periodicitySelect"
                    />
                </app-table-header-select>
            </app-table-header-item>

          <dropdown-input
              :label="translations.format"
              :options="autocompleteFormatSource"
              :selected="selectedFormatSource.map(v => v.labelName)"
              @autocompleteValueChanged="formatLabelSelected"
              @removeItem="removeFormatLabel"
              @removeAll="removeAllFormatLabels"
          />
          <dropdown-input
              :label="translations.labels"
              :options="autocompleteContentSource"
              :selected="selectedContentSource.map(v => v.labelName)"
              @autocompleteValueChanged="contentLabelSelected"
              @removeItem="removeContentLabel"
              @removeAll="removeAllContentLabels"
          />
          <app-table-header-item class="field_left">
            <app-table-header-select :label="translations.createdBy">
              <app-select
                  id="createdBy"
                  :options="createdOptions"
                  selectId="createdSelect"
                  name="createdSelect"
                  v-model="createdSelect"
              />
            </app-table-header-select>
            <app-table-header-select :label="translations.language">
              <app-select
                  id="languages"
                  :options="languageOptions"
                  selectId="languageSelect"
                  name="languageSelect"
                  v-model="languageSelect"
              />
            </app-table-header-select>
          </app-table-header-item>
        </template>

        <app-table-column first sortable attribute="name" :label="translations.name">
          <template slot-scope="row">
            <strong>
              <a>{{ row.name || row.originalName || row.firstTranslationName }}</a>
            </strong>
            <span class="name_icon" title="name">{{ row.name ? 'N' : row.originalName ? 'ON' : row.firstTranslationName ? 'FTN' : '' }}</span>
          </template>
        </app-table-column>
        <app-table-column sortable sort-name="formatLabel" attribute="formatLabelId" :label="translations.format">
          <template slot-scope="row">
            {{ formatLabelsMap[row.formatLabelId] }}
          </template>
        </app-table-column>
        <app-table-column :label="translations.labels">
          <template slot-scope="row">
            {{ row.contentLabelIds.map(v => contentLabelsMap[v]).join(', ') }}
          </template>
        </app-table-column>
        <app-table-column sortable attribute="numberOfBroadcasts" :label="translations.numberOfBroadcasts" />
        <app-table-column sortable sort-name="created" :label="translations.created">
          <template slot-scope="row">
            <div v-html="formatDate(row._created)" />
          </template>
        </app-table-column>
        <app-table-column sortable sort-name="updated" :label="translations.updated">
          <template slot-scope="row">
            <div v-html="formatDate(row._updated)" />
          </template>
        </app-table-column>
        <app-table-column :label="translations.creationType">
          <template slot-scope="row">
            {{ !row.automaticallyCreated ? translations.manuallyCreated : row.manuallyUpdated ? translations.manuallyUpdated : translations.automaticallyCreated }}
          </template>
        </app-table-column>
        <app-table-column sortable attribute="rating" :label="translations.rating" />

        <template slot="footer">
          <p class="check_all">
            <app-checkbox
                checkboxId="check-all"
                name="check-all"
                :checked="hasSelectedAll"
                @change="selectAll"
            />
            <app-button @click="deleteShows" v-translate>Smazat</app-button>
          </p>
          <app-table-pagination
            v-if="itemsMetadata.totalCount > params.limit"
            :page="listPage"
            :itemsPerPage="params.limit"
            :totalCount="itemsMetadata.totalCount"
            @pageChanged="tablePageChangedLocal"
          />
        </template>
      </app-table>
    </app-content-body>
  </app-content>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import getItemsListMixin from 'mixins/getItemsListMixin'
import appTitleMixin from 'mixins/appTitleMixin'
import formatDateMixin from 'mixins/formatDateMixin'
import { AUTOCOMPLETE_TITLE } from '@/symbols/autocomplete'
import DropdownInput from '@/components/DropdownInput'
import { showDeleteDialog } from '../../utils/dialog'

const itemsListMixin = getItemsListMixin({
  apiMethodsSuffix: 'Shows',
  preventFetchOnCreated: true
})

export default {
  name: 'shows',
  components: { DropdownInput },
  mixins: [ formatDateMixin, itemsListMixin, appTitleMixin()],
  created() {
    this.getLanguages({
      params: {
        idLanguage: localStorage.ui_language_id,
        order: 'languageNames'
      }
    })
    this.getLabelsForCurrentLanguage()
    if (this.params.createdBy) {
      this.createdSelect = this.params.createdBy
    }
    if (this.params.periodicity) {
      this.periodicitySelect = this.params.periodicity
    }
    if (this.params.languageFilter) {
      this.languageSelect = this.params.languageFilter
    }
    if (this.params.fulltextSearch !== undefined && this.params.fulltextSearch !== null) {
      this.fulltextSearch = this.params.fulltextSearch
    } else {
      this.fulltextSearch = true
    }
    this.filterShows(false, true)
  },
  data() {
    return {
      checkedRows: [],
      fetchTimeoutId: null,
      createdSelect: '',
      periodicitySelect: '',
      languageSelect: '',
      extended: false,
      selectedFormatLabels: [],
      selectedContentLabels: [],
      fulltextSearch: null,
      dataPending: false
    }
  },
  computed: {
    ...mapState({
      labels: state => state.labels.labels,
      labelsPending: state => state.labels.pending.labels,
      currentLanguageId: state => state.uiLanguages.currentLanguageId,
      languages: state => state.languages.languages
    }),
    hasSelectedAll() {
      return (
        this.checkedRows.length > 0 &&
          this.items.every(v => this.checkedRows.includes(v._id))
      )
    },
    autocompleteFormatSource() {
      if (!this.labels) {
        return []
      }

      return this.labels
        .filter(v => v.type === 'LABEL_TYPE_FORMAT' && !this.params.formatLabels.split(',').includes(v._id + '')).map(item => ({
          ...item,
          [AUTOCOMPLETE_TITLE]: item.labelName
        }))
    },
    selectedFormatSource() {
      if (!this.labels) {
        return []
      }

      return this.labels
        .filter(v => v.type === 'LABEL_TYPE_FORMAT' && this.params.formatLabels.split(',').includes(v._id + '')).map(item => ({
          ...item,
          [AUTOCOMPLETE_TITLE]: item.labelName
        }))
    },
    autocompleteContentSource() {
      if (!this.labels) {
        return []
      }

      return this.labels
        .filter(v => v.type === 'LABEL_TYPE_CONTENT' && !this.params.contentLabels.split(',').includes(v._id + '')).map(item => ({
          ...item,
          [AUTOCOMPLETE_TITLE]: item.labelName
        }))
    },
    selectedContentSource() {
      if (!this.labels) {
        return []
      }

      return this.labels
        .filter(v => v.type === 'LABEL_TYPE_CONTENT' && this.params.contentLabels.split(',').includes(v._id + '')).map(item => ({
          ...item,
          [AUTOCOMPLETE_TITLE]: item.labelName
        }))
    },
    createdOptions() {
      return [
        { name: this.translations.none, value: '' },
        { name: this.translations.automatically, value: 'AUTOMATICALLY' },
        { name: this.translations.manually, value: 'MANUALLY' }
      ]
    },
    periodicOptions() {
      return [
        { name: this.translations.none, value: '' },
        { name: this.translations.periodical, value: 'PERIODIC' },
        { name: this.translations.nonPeriodical, value: 'NONPERIODIC' }
      ]
    },
    translations() {
      return {
        addShow: this.$gettext('Přidat pořad'),
        searchByName: this.$gettext('Hledat v názvu'),
        searchByContent: this.$gettext('Hledat v obsahu'),
        periodicity: this.$gettext('Periodicita'),
        periodical: this.$gettext('Periodický'),
        nonPeriodical: this.$gettext('Neperiodický'),
        name: this.$gettext('Název'),
        format: this.$gettext('Formát'),
        labels: this.$gettext('Štítky'),
        numberOfBroadcasts: this.$gettext('Počet vysílání'),
        rating: this.$gettext('Hodnocení'),
        created: this.$gettext('Založeno'),
        updated: this.$gettext('Upraveno'),
        createdBy: this.$gettext('Vytvořeno'),
        language: this.$gettext('Jazyk'),
        none: this.$gettext('Nevybráno'),
        automatically: this.$gettext('Automaticky'),
        manually: this.$gettext('Manuálně'),
        automaticallyCreated: this.$gettext('Automaticky vytvořeno'),
        manuallyCreated: this.$gettext('Manuálně vytvořeno'),
        manuallyUpdated: this.$gettext('Manuálně upraveno'),
        creationType: this.$gettext('Typ vytvoření'),
        removeAll: this.$gettext('Odstranit vše')
      }
    },
    formatLabelsMap() {
      return this.formatLabelsOptions.reduce((obj, v) => {
        obj[v.value] = v.name
        return obj
      }, {})
    },
    languageOptions() {
      return [{ name: 'Neurčeno', value: null }].concat(this.languages.map(v => ({ name: v.languageName, value: v._id })))
    },
    formatLabelsOptions() {
      if (!this.labels) {
        return []
      }
      return this.labels
        .filter(v => v.type === 'LABEL_TYPE_FORMAT')
        .reduce((arr, v) => [...arr, { name: v.labelName, value: v._id }], [])
    },
    contentLabelsMap() {
      return this.contentLabelsOptions.reduce((obj, v) => {
        obj[v.value] = v.name
        return obj
      }, {})
    },
    contentLabelsOptions() {
      if (!this.labels) {
        return []
      }
      return this.labels
        .filter(v => v.type === 'LABEL_TYPE_CONTENT')
        .reduce((arr, v) => [...arr, { name: v.labelName, value: v._id }], [])
    },
    currentTitle() {
      return [
        this.$gettext('Pořady')
      ]
    }
  },
  methods: {
    ...mapActions([
      'deleteShows',
      'getLabels',
      'getLanguages'
    ]),
    shouldRefreshAfterDelete() {
      return true
    },
    selectRow(row, checked) {
      if (checked) {
        this.checkedRows.push(row._id)
      } else {
        const index = this.checkedRows.indexOf(row._id)
        if (index !== -1) {
          this.checkedRows.splice(index, 1)
        }
      }
    },
    deleteShows() {
      showDeleteDialog(this, {
        apiMethod: 'deleteShows',
        params: {
          ids: this.checkedRows
        },
        shouldRefresh: true,
        refresh: {
          apiMethod: 'getShows',
          params: this.params
        }
      }, this.getDeleteMessage())
    },
    selectAll(checked) {
      if (checked) {
        this.items.forEach(v => {
          if (this.checkedRows.indexOf(v._id) === -1) {
            this.checkedRows.push(v._id)
          }
        })
      } else {
        this.checkedRows = []
      }
    },
    async filterShows(resetPage = false, created = false) {
      if (created) {
        this.dataPending = true
        const result = await this.fetchItems(true).then((res) => {
          this.dataPending = false
        })
      } else if (this.dataPending) {

      } else {
        this.fetchItems(true)
      }
    },
    removeFormatLabel(item) {
      this.selectedFormatLabels = this.selectedFormatLabels.filter(v => v.name !== item)
      this.params.formatLabels = this.selectedFormatLabels.map(v => v.value).join(',')
      this.setFetchTimeout()
    },
    removeAllFormatLabels() {
      this.params.formatLabels = ''
      this.selectedFormatLabels = []
      this.setFetchTimeout()
    },
    formatLabelSelected(id) {
      if (id !== null) {
        this.selectedFormatLabels.push(this.formatLabelsOptions.filter(v => v.value === id)[0])
        this.params.formatLabels = this.selectedFormatLabels.map(v => v.value).join(',')
        this.setFetchTimeout()
      }
      window.setTimeout(function () {
        document.getElementById('show-name-search').focus()
      }, 0)
    },
    removeContentLabel(item) {
      this.selectedContentLabels = this.selectedContentLabels.filter(v => v.name !== item)
      this.params.contentLabels = this.selectedContentLabels.map(v => v.value).join(',')
      this.setFetchTimeout()
    },
    removeAllContentLabels() {
      this.params.contentLabels = ''
      this.selectedContentLabels = []
      this.setFetchTimeout()
    },
    contentLabelSelected(id) {
      if (id !== null) {
        this.selectedContentLabels.push(this.contentLabelsOptions.filter(v => v.value === id)[0])
        this.params.contentLabels = this.selectedContentLabels.map(v => v.value).join(',')
        this.setFetchTimeout()
      }
      window.setTimeout(function () {
        document.getElementById('show-name-search').focus()
      }, 0)
    },
    setFetchTimeout() {
      clearTimeout(this.fetchTimeoutId)
      this.fetchTimeoutId = setTimeout(() => this.filterShows(true), 500)
    },
    getDeleteMessage(item) {
      if (item !== undefined) {
        return this.$gettext('Počet navázaných vysílání') + ': ' + item.numberOfBroadcasts
      }
    },
    getLabelsForCurrentLanguage() {
      this.getLabels({
        params: {
          idLanguage: this.currentLanguageId,
          order: 'labelNames'
        }
      })
    },
    onFocus() {
      this.extended = true
    },
    onBlur() {
      this.extended = false
    },
    formatLabelIdToName(id) {
      return this.labels.filter(v => v._id === parseInt(id))[0].labelName
    },
    tablePageChangedLocal(page, fetchItems = true) {
      this.checkedRows = []
      this.tablePageChanged(page, fetchItems)
    }
  },
  watch: {
    currentLanguageId() {
      this.getLabelsForCurrentLanguage()
    },
    createdSelect() {
      this.params.createdBy = this.createdSelect
      this.filterShows(true)
    },
    periodicitySelect() {
      this.params.periodicity = this.periodicitySelect
      this.filterShows(true)
    },
    languageSelect() {
      this.params.languageFilter = this.languageSelect
      this.filterShows(true)
    },
    selectedFormatSource() {
      if (this.params.formatLabels !== '') {
        this.selectedFormatLabels = this.formatLabelsOptions.filter(v => this.params.formatLabels.split(',').includes(v.value + ''))
      }
      if (this.params.contentLabels !== '') {
        this.selectedContentLabels = this.contentLabelsOptions.filter(v => this.params.contentLabels.split(',').includes(v.value + ''))
      }
    },
    fulltextSearch(value) {
      this.params.fulltextSearch = value
      this.filterShows(true)
    }
  }
}
</script>
